/**
 * One dataset as a row (client, 2026-09-16: *"still not happy with our modernity and
 * creativity on the catalogue"*).
 *
 * The grid is how a catalogue is *browsed*; this is how it is *worked*. A reader comparing
 * ninety-five datasets by source, shape, classification and date wants them in columns that
 * line up, not in cards that each need their own scan — so the list is a fixed 56 px rhythm,
 * hairline-separated, with every field in the same place on every row.
 *
 * Three decisions worth naming:
 *
 * * **Equal height, always.** 56 px flat, titles clipped to one line. A row that grew for a
 *   long title would break the only thing a list has over a grid, which is that the eye can
 *   run down a column without tracking.
 * * **The title is the link.** In the grid the whole card is a `role="button"`; here the
 *   actions sit *on* the row, so a whole-row click target would swallow them. The title is a
 *   real `<button>`, tabbable on its own, and the rest of the row is plain text.
 * * **The same handlers and the same test ids as the card** (`card-preview-…`,
 *   `card-details-…`). Preview on map is one rule (`preview.ts`) and a reader switching
 *   layouts has not switched products; only one layout is ever mounted, so the two cannot
 *   collide.
 *
 * The 3 px left edge is the row's domain, the same colour and the same width the card's edge
 * carries — it is what keeps a list of ninety-five rows readable as a grouped thing.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { DatasetDomainFacet, DatasetRow } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { Chip } from '../../components/Chip';
import { dataTypeIcon, formatUpdated } from './catalogue';

export interface DatasetListRowProps {
  row: DatasetRow;
  /** The row's domain facet — its colour is the left edge. Absent → no edge, never a grey one. */
  domain?: DatasetDomainFacet;
  onOpen: (id: string) => void;
  onPreview: (row: DatasetRow) => void;
}

export function DatasetListRow({ row, domain, onOpen, onPreview }: DatasetListRowProps) {
  const { t, i18n } = useTranslation();
  const typeLabel = t(`catalogue.type.${row.data_type}`);
  // A custom property rather than a `borderLeftColor`: the edge is drawn by a pseudo-element
  // (`.ds-row::before`), which cannot be given an inline style of its own.
  const style = domain ? ({ '--ds-domain': domain.color } as CSSProperties) : undefined;

  return (
    <div className="ds-row" data-testid={`ds-row-${row.id}`} style={style}>
      <span className="ds-row-icon" role="img" aria-label={typeLabel} title={typeLabel}>
        <Icon name={dataTypeIcon(row.data_type)} size={15} />
      </span>

      <button
        type="button"
        className="ds-row-title"
        data-testid={`ds-row-title-${row.id}`}
        onClick={() => onOpen(row.id)}
      >
        {row.title}
      </button>

      <span className="ds-row-meta">
        <span className="ds-row-src">{row.source_org}</span>
        <span className="ds-row-type">{typeLabel}</span>
      </span>

      <span className="ds-row-cls">
        <Chip kind={row.classification} />
      </span>

      <span className="ds-row-upd">{formatUpdated(row.updated_at, i18n.language)}</span>

      <span className="ds-row-acts">
        <button
          type="button"
          className="btn ghost sm"
          data-testid={`card-preview-${row.id}`}
          // §F9, exactly as on the card: the server says whether this row has anywhere to
          // be previewed, and a table-only record has not.
          disabled={row.preview === null}
          title={row.preview === null ? t('catalogue.record.tabularPreview') : undefined}
          onClick={() => onPreview(row)}
        >
          <Icon name="map" size={13} /> {t('catalogue.record.preview')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          data-testid={`card-details-${row.id}`}
          onClick={() => onOpen(row.id)}
        >
          {t('catalogue.card.details')}
        </button>
      </span>
    </div>
  );
}

export default DatasetListRow;
